import { RippleTheme } from "./defaultRippleTheme";
import { ButtonHoverEffect, ButtonShape, ButtonSize, ButtonVariant } from "../../components/Button/Button";

export type ButtonBaseStyle = {
  backgroundColor?: string;
  color?: string;
  focusRing?: string;
  fontSize?: number;
  height?: string;
  padding?: string;
  ripple?: Partial<RippleTheme>;
};

export type ButtonPropStyle = {
  shape?: Partial<Record<ButtonShape, ButtonStyle>>;
  size?: Partial<Record<ButtonSize, ButtonStyle>>;
  variant?: Partial<Record<ButtonVariant, ButtonStyle>>;
};

export type ButtonStateStyle = Partial<
  Record<"disabled" | "hover" | "withAddon", ButtonBaseStyle>
>;

export type ButtonStyle = ButtonBaseStyle & ButtonStateStyle;

export type ButtonTheme = {
  current?: ButtonStyle;
  defaultProps?: ButtonThemeDefaultProps;
  defaultStyle?: ButtonStyle;
} & ButtonPropStyle;

export type ButtonThemeDefaultProps = {
  hoverEffect?: ButtonHoverEffect;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

const primaryDangerVariant: ButtonStyle = {
  backgroundColor: "--primary",
  color: "--white",
  focusRing: "--primary-200",
  hover: { backgroundColor: "--primary-600" },
  ripple: { circleFill: "--primary", fill: "--primary-600" },
};

export const defaultButtonTheme: ButtonTheme = {
  defaultProps: {
    hoverEffect: 'color',
    size: 'm',
    variant: 'secondary'
  },
  defaultStyle: {
    disabled: {
      backgroundColor: "--neutral-300",
      color: "--neutral-a24",
    },
    fontSize: 14,
  },
  shape: {
    circle: { padding: "0" },
    normal: {},
  },
  size: {
    xs: {
      fontSize: 12,
      height: "3sp",
      padding: "1.5sp",
      withAddon: { padding: "1sp" },
    },
    s: {
      height: "4sp",
      padding: "2sp",
      withAddon: { padding: "1.5sp" },
    },
    m: {
      height: "5sp",
      padding: "2.5sp",
      withAddon: { padding: "2sp" },
    },
    l: {
      height: "6sp",
      padding: "3sp",
      withAddon: { padding: "2.5sp" },
    },
    xl: {
      fontSize: 16,
      height: "7sp",
      padding: "3.5sp",
      withAddon: { padding: "3sp" },
    },
  },
  variant: {
    primary: primaryDangerVariant,
    secondary: {
      backgroundColor: "--neutral-300",
      color: "--black",
      focusRing: "--accent-200",
      hover: { backgroundColor: "--neutral-400" },
      ripple: { circleFill: "--neutral-300", fill: "--neutral-400" },
    },
    tertiary: {
      backgroundColor: "--black",
      color: "--white",
      focusRing: "--accent-200",
      hover: { backgroundColor: "--neutral-700" },
      ripple: { circleFill: "--black", fill: "--neutral-700" },
    },
    flat: {
      backgroundColor: "transparent",
      color: "--black",
      focusRing: "--accent-100",
      hover: { color: "--primary" },
      ripple: { fill: "--neutral-300" },
    },
    accent: {
      backgroundColor: "--accent",
      color: "--white",
      focusRing: "--accent-200",
      hover: { backgroundColor: "--accent-600" },
      ripple: { circleFill: "--accent", fill: "--accent-600" },
    },
    success: {
      backgroundColor: "--success",
      color: "--white",
      focusRing: "--success-300",
      hover: { backgroundColor: "--success-400" },
      ripple: { circleFill: "--success", fill: "--success-400" },
    },
    alert: {
      backgroundColor: "--alert",
      color: "--white",
      focusRing: "--alert-300",
      hover: { backgroundColor: "--alert-600" },
      ripple: { circleFill: "--alert", fill: "--alert-600" },
    },
    danger: primaryDangerVariant,
  },
};
